import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MenuItem } from 'primeng/api';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface MiniModePageOption {
  key: string;
  label: string;
  route: string;
}

export interface UserProfile {
  emailAddress: string;
  firstName: string;
  lastName: string;
  department: string | null;
  miniModePageKeys: string[];
  availableMiniModePages: MiniModePageOption[];
}

export const eligibleMiniModePages: MiniModePageOption[] = [
  { key: 'reconciliation', label: 'Reconcile', route: '/reconciliation' },
  { key: 'transactions', label: 'Transactions', route: '/reconciliation/transactions' },
  { key: 'purchase-orders', label: 'Purchase Orders', route: '/reconciliation/purchase-orders' },
  { key: 'rule-management', label: 'Rule Management', route: '/reconciliation/rule-management' },
  { key: 'manual-reconciliation-ledger', label: 'Manual Reconciliation Ledger', route: '/reconciliation/manual-reconciliation-ledger' },
  { key: 'portal', label: 'Portal Management', route: '/portal' },
  { key: 'company', label: 'Business Entities', route: '/company' },
  { key: 'members', label: 'Members', route: '/members' },
  { key: 'banks', label: 'Financial Institutions', route: '/banks' },
  { key: 'report-generation', label: 'Report Generator', route: '/report-generation' }
];

const protectedMiniModeKeys = new Set(['dashboard', 'credit-cards', 'cc-transactions', 'audit-trail', 'profile']);

export function routeKeyForPath(path: string): string | null {
  if (path === '/') return 'dashboard';
  if (path === '/credit-cards') return 'credit-cards';
  if (path === '/cc-transactions') return 'cc-transactions';
  if (path === '/audit-trail') return 'audit-trail';
  if (path === '/profile') return 'profile';
  return eligibleMiniModePages.find((page) => path === page.route || path.startsWith(`${page.route}/`))?.key ?? null;
}

export function miniModeLandingRoute(selectedKeys: string[]): string {
  const page = selectedKeys.map((key) => eligibleMiniModePages.find((candidate) => candidate.key === key)).find(Boolean) ?? eligibleMiniModePages[0];
  return `${page.route}?mode=mini`;
}

export function filterMenuForMiniMode(items: MenuItem[], selectedKeys: string[]): MenuItem[] {
  const selected = new Set(selectedKeys);
  return items
    .map((item) => {
      if (item.items) {
        const children = filterMenuForMiniMode(item.items, selectedKeys);
        return children.length ? { ...item, items: children } : null;
      }
      const route = item.routerLink?.[0];
      const key = typeof route === 'string' ? routeKeyForPath(route) : null;
      return key && selected.has(key) ? item : null;
    })
    .filter((item): item is MenuItem => item !== null);
}

@Injectable({ providedIn: 'root' })
export class MiniModeService {
  private readonly apiUrl = `${environment['portalApiUrl']}/api/user-profile`;
  private profile: UserProfile | null = null;

  constructor(private readonly http: HttpClient) {}

  isMiniMode(): boolean {
    return new URLSearchParams(window.location.search).get('mode')?.toLowerCase() === 'mini';
  }

  getProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(this.apiUrl).pipe(tap((profile) => (this.profile = profile)));
  }

  updateProfile(profile: Pick<UserProfile, 'firstName' | 'lastName' | 'department' | 'miniModePageKeys'>): Observable<UserProfile> {
    return this.http.put<UserProfile>(this.apiUrl, profile).pipe(tap((updated) => (this.profile = updated)));
  }

  isPathAllowed(path: string): boolean {
    if (!this.isMiniMode()) return true;
    const key = routeKeyForPath(path);
    return !!key && !protectedMiniModeKeys.has(key) && !!this.profile?.miniModePageKeys.includes(key);
  }
}
